import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { Formik, Field, Form } from "formik";

import Layout from "../reusable-components/Layout";
import WaterDropGridVideo from "../reusable-components/custom-component/WaterDropGridVideo";
import { useCloudinaryUpload } from "../utilities/useCloudinaryUpload";

function HomePage() {
  const fileInputRef = useRef(null);
  const [data, setData] = useState([]);
  const [videoModel, setVideoModel] = useState("sora");
  const [showText, setShowText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [frame, setFrame] = useState({
    key: "10 seconds",
    value: 10,
  });
  const [quality, setQuality] = useState({
    credit: 150,
    value: "standard",
    text: "standard - 150 credits",
  });
  const [isStory, setIsStory] = useState(false);
  const [openAIWidget, setOpenAiWidget] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalIndex, setModalIndex] = React.useState(0);
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const isMobileDevice =
    window.matchMedia("(max-width: 430px)").matches &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);
  const PromptSchema = Yup.object().shape({
    prompt: Yup.string().min(3, "Too Short!").required("Required"),
    promptType: Yup.string().required("Required"),
    aspectRatio: Yup.string().required("Required"),
  });
  const { upload, progress, setProgress } = useCloudinaryUpload();
  const GOOGLE_SHEET_API =
    "https://script.google.com/macros/s/AKfycbwF_DhPaaSL50yegSKx6ial8yCcupmF9RIOVi2DDLXbsVS4NUPzRhh2RwCGEcQqPkytqg/exec";

  async function isBrokenVideoUrl(url) {
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Range: "bytes=0-1024",
        },
      });

      if (!res.ok) return true;

      const contentType = res.headers.get("content-type");

      if (!contentType || !contentType.startsWith("video/")) {
        return true;
      }

      return false;
    } catch {
      return true;
    }
  }

  useEffect(() => {
    let interval;

    const fetchData = async () => {
      const pendingRowId = localStorage.getItem("pendingRowId");

      // Define unique callback
      const callbackName = `callback_${Date.now()}`;
      window[callbackName] = async (result) => {
        if (pendingRowId) {
          const row = result.find((item) => String(item.id) === pendingRowId);
          if (row && row.video) {
            setLoading(false);
            localStorage.removeItem("pendingRowId");
          }
        }
        const workingVideos = [];
        for (const res of result) {
          const isBroken = await isBrokenVideoUrl(res.video);
          if (!isBroken) {
            workingVideos.push(res);
          }
        }
        setData(workingVideos.reverse());

        // Cleanup script and callback
        const script = document.getElementById(callbackName);
        if (script) script.remove();
        delete window[callbackName];
      };

      const script = document.createElement("script");
      script.src = `${GOOGLE_SHEET_API}?callback=${callbackName}`;
      script.id = callbackName;
      document.body.appendChild(script);
    };

    // First fetch
    if (!openAIWidget) {
      fetchData();

      // Poll every 5 seconds
      interval = setInterval(fetchData, 5000);

      return () => clearInterval(interval); // cleanup interval
    }
  }, [openAIWidget]);

  const generateId = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  };

  const videoModelOptions = [
    { text: "sora", display: "sora-2-pro" },
    { text: "veo", display: "veo-fal-ai" },
  ];

  const soraFrameOptions = [
    { key: "10 seconds", value: 10 },
    { key: "15 seconds", value: 15 },
    { key: "25 seconds", value: 25 },
  ];

  const soraCreditOptions = {
    "10 seconds": {
      standard: {
        credit: 150,
        value: "standard",
        text: "standard - 150 credits",
      },
      high: {
        credit: 330,
        value: "high",
        text: "high - 330 credits",
      },
    },
    "15 seconds": {
      standard: {
        credit: 270,
        value: "standard",
        text: "standard - 270 credits",
      },
      high: {
        credit: 630,
        value: "high",
        text: "high - 630 credits",
      },
    },
    "25 seconds": {
      standard: {
        credit: 270,
        value: "standard",
        text: "standard - 270 credits",
      },
      high: {
        credit: 630,
        value: "high",
        text: "high - 630 credits",
      },
    },
  };

  const veoFrameOptions = [
    { key: "4 seconds", value: 4 },
    { key: "6 seconds", value: 6 },
    { key: "8 seconds", value: 8 },
  ];

  const veoImageFrameOptions = [
    { key: "5 seconds", value: 5 },
    { key: "10 seconds", value: 10 },
  ];

  const veoCreditOptions = {
    "4 seconds": {
      standard: {
        credit: 150,
        value: "standard",
        text: "standard - 150 credits",
      },
      high: {
        credit: 330,
        value: "high",
        text: "high - 330 credits",
      },
    },
    "6 seconds": {
      standard: {
        credit: 270,
        value: "standard",
        text: "standard - 270 credits",
      },
      high: {
        credit: 630,
        value: "high",
        text: "high - 630 credits",
      },
    },
    "8 seconds": {
      standard: {
        credit: 270,
        value: "standard",
        text: "standard - 270 credits",
      },
      high: {
        credit: 630,
        value: "high",
        text: "high - 630 credits",
      },
    },
  };

  async function handleRequest(value) {
    setLoading(true);

    try {
      const rowId = generateId();
      const queryString = `
        id=${rowId}
        &type=${encodeURIComponent(value?.promptType)}
        &prompt=${encodeURIComponent(value?.prompt)}
        &image=${imageUrl}
        &processed=${false}
        &aspectRatio=${value?.aspectRatio}
        &quality=${
          videoModel === "sora"
            ? quality.value
            : quality.value === "standard"
            ? "720p"
            : "1080p"
        }
        &frame=${frame.value}
        &prompt2=${encodeURIComponent(value?.prompt2)}
        &model=${videoModel}
      `.replace(/\s+/g, "");

      const res = await fetch(GOOGLE_SHEET_API, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: queryString,
      });
      const data = await res.text();
      localStorage.setItem("pendingRowId", rowId);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setProgress([]);
      setIsStory(false);
      setFrame({
        key: "10 seconds",
        value: 10,
      });
      setQuality({
        credit: 150,
        value: "standard",
        text: "standard - 150 credits",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      alert(`${data} Request Submitted!`);
    } catch (error) {
      console.error("***ERROR***", error);
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await upload(file, "users/avatars");
      setImageUrl(url);
      console.log("Uploaded URL:", url);
    } catch (err) {
      console.error("Upload failed:", err);
    }
  };

  let frameOption = soraFrameOptions;
  let creditOptions = soraCreditOptions;

  if (videoModel === "veo") {
    frameOption = veoFrameOptions;
    creditOptions = veoCreditOptions;
  }

  if (videoModel === "veo" && imageUrl) {
    frameOption = veoImageFrameOptions;
  }

  return (
    <>
      <Helmet>
        <title>Wiingr | Home</title>
      </Helmet>
      <Layout
        isHomePage={true}
        openAIWidget={openAIWidget}
        setOpenAiWidget={setOpenAiWidget}
      >
        <div className="relative flex flex-col sm:flex-row overflow-hidden h-[100vh]">
          <div
            className={`relative h-[100vh] overflow-hidden sm:flex-none ${
              showSidebar
                ? "w-[100%] sm:w-[50%] md:w-[55%] lg:w-[60%] xl:w-[65%] 2xl:w-[80%] flex-[0.8]"
                : "w-[100%] pb-16"
            }`}
          >
            <div className="absolute right-0 left-0 top-0 bottom-0 w-full">
              <WaterDropGridVideo />
            </div>
            {!isMobileDevice && (
              <div className="relative h-full w-full p-1 py-20 xl:px-2 bg-black/70 selection:bg-white/30 selection:text-black mx-auto overflow-auto no-scrollbar">
                <div className="mt-10 mb-16 w-full mx-auto flex flex-col items-center">
                  <div
                    className="text-sm bg-clip-text text-transparent cursor-pointer 
  bg-gradient-to-r from-pink-500 via-yellow-400 to-purple-500 
  bg-[length:200%_200%] animate-gradient-move"
                  >
                    <p className="text-2xl md:text-4xl uppercase font-saira text-center">
                      Automating Customer Connections
                    </p>
                  </div>

                  <p className="uppercase text-xs text-white/50 font-saira mt-0 md:mt-[-8px]">
                    Web | Mobile | AI-Powered Call Solutions for Businesses and
                    Startups
                  </p>
                  <div className="flex flex-row items-end mt-10 mb-5 gap-20">
                    <div
                      className="tracking-wider font-saira bg-clip-text text-transparent cursor-pointer 
  bg-gradient-to-r from-white via-white/20 to-white/100
  bg-[length:200%_200%] animate-gradient-move flex flex-col items-center"
                    >
                      <p className="text-xl md:text-xl font-[400]">
                        Unleash Your{" "}
                        <span className="font-Inspiration text-5xl tracking-wider">
                          Creativity!
                        </span>
                      </p>
                      <p className="text-xs md:text-[14px]">
                        Hit the + button and let{" "}
                        <span className="border-b-[0.5px] border-neutral-500 font-thin">
                          AI craft stunning cinematic videos
                        </span>{" "}
                        from your ideas
                      </p>
                    </div>
                    <div
                      onClick={() => !showSidebar && setShowSidebar(true)}
                      className={`w-fit h-fit ${
                        showSidebar ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className={`size-6 rounded-full p-1 transition-all duration-1000 ${
                          showSidebar
                            ? "text-white/10 bg-white/5"
                            : "text-yellow-600 bg-yellow-400/10 hover:bg-yellow-400/5 hover:text-yellow-600/70"
                        }`}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4.5v15m7.5-7.5h-15"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="w-fit h-fit flex flex-row-reverse flex-wrap gap-5 sm:gap-10 mx-auto justify-center">
                  {data.map((dt, idx) => (
                    <div
                      key={idx}
                      className="relative w-[150px] h-[250px] sm:h-[220px] sm:w-[150px] rounded-md overflow-hidden"
                    >
                      <div
                        className={`absolute z-10 top-0 left-0 bottom-0 overflow-auto no-scrollbar font-thin  transition-all duration-1000 ${
                          showText === idx
                            ? "opacity-1 inline-block"
                            : "opacity-0 hidden"
                        } `}
                      >
                        <p className="bg-white/80 text-black overflow-auto p-2 leading-4 text-[12px] tracking-wide h-full">
                          <span className="block w-full font-semibold pb-[1px] mb-2 border-b-[0.5px] border-black/10">
                            Prompt:
                          </span>{" "}
                          {dt.prompt} {dt?.prompt2}
                        </p>
                      </div>
                      <div className=" rounded-md overflow-hidden h-full w-full">
                        <video
                          className="h-full w-full object-cover"
                          controls
                          autoPlay={false}
                          src={dt.video}
                        />
                      </div>
                      <div className="absolute z-10 top-1 right-2 cursor-pointer flex flex-row items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-3 hover:bg-black/80 bg-black/50 rounded-full p-[2px] transition-all duration-1000 cursor-pointer"
                          onClick={() => {
                            setModalIndex(idx);
                            setModalIsOpen(true);
                          }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                          />
                        </svg>
                        <span
                          onClick={() => {
                            if (showText !== "") {
                              setShowText("");
                            } else {
                              setShowText(idx);
                            }
                          }}
                          className={`cursor-pointer relative inline-flex rounded-full h-2 w-2 transition-all duration-1000 ${
                            showText === idx
                              ? "bg-orange-900 hover:bg-orange-600"
                              : "bg-orange-600 hover:bg-orange-900"
                          }`}
                        ></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {modalIsOpen && (
              <div className="absolute z-20 top-0 bottom-0 left-0 right-0 rounded-md w-full h-full flex flex-col items-center justify-center py-20 bg-black/90">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6 absolute top-20 left-10 text-white/30 cursor-pointer hover:text-white/10 transition-all duration-1000"
                  onClick={() => setModalIsOpen(false)}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                <video
                  className={`object-cover ${
                    data[modalIndex].aspectRatio === "portrait"
                      ? "h-full"
                      : "w-[90%] lg:w-[80%] xl:w-[75%]"
                  }`}
                  controls
                  autoPlay={false}
                  src={data[modalIndex]?.video}
                />
              </div>
            )}
            {isMobileDevice && (
              <div className="flex flex-row items-center justify-center h-[100%]">
                <p className="py-2 px-5 text-center text-white text-2xl">
                  You can access video generation features on both desktop and
                  tablet platforms.
                </p>
              </div>
            )}
          </div>
          {!isMobileDevice && (
            <Formik
              initialValues={{
                prompt: "",
                prompt2: "",
                promptType: "",
                aspectRatio: "",
              }}
              validationSchema={PromptSchema}
              onSubmit={async (values, { resetForm }) => {
                handleRequest(values);
                resetForm();
              }}
            >
              <div
                className={`relative flex-[0.2] sm:flex-none ${
                  showSidebar
                    ? "inline-block w-[100%] pb-10 sm:pb-0 sm:w-[50%] md:w-[45%] lg:w-[40%] xl:w-[35%] 2xl:w-[20%] h-[100vh]"
                    : "hidden"
                }`}
              >
                <div className="text-neutral-800">
                  <div className="font-Inspiration absolute top-[9rem] left-[45%]">
                    <p className="text-[4rem] tracking-wide -rotate-12">Just</p>
                  </div>
                  <div className="font-Inspiration absolute top-[12rem] left-[35%] sm:left-[30%]">
                    <p className="text-[4rem] tracking-widest -rotate-12">
                      Prompt
                    </p>
                  </div>
                  <div className="font-Inspiration absolute top-[14.5rem] left-[47%]">
                    <p className="text-[4rem] tracking-wide rotate-12">it</p>
                  </div>
                  <div class="absolute top-[15rem] left-[48%] flex items-center justify-center">
                    {loading && (
                      <span class="absolute inline-flex h-5 w-5 rounded-full bg-green-600 opacity-75 animate-ping"></span>
                    )}
                    <span class="relative inline-flex rounded-full h-1 w-1 bg-green-600"></span>
                  </div>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="hidden sm:inline-block size-3 text-white/50 cursor-pointer absolute top-20 left-4"
                  onClick={() => setShowSidebar(false)}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m5.25 4.5 7.5 7.5-7.5 7.5m6-15 7.5 7.5-7.5 7.5"
                  />
                </svg>

                <Form className="bg-black/70 px-1 sm:px-5 flex flex-col justify-end pb-16 pt-3 gap-[5px] shadow-xl shadow-white/10 font-thin text-white/50 h-full">
                  <div className="flex flex-col">
                    <div className="w-full ">
                      <Field name="prompt">
                        {({ field, form: { touched, errors }, meta }) => (
                          <div
                            className={`flex-[0.5] mb-5 sm:flex-none ${
                              isStory ? "h-[100px]" : "h-[200px]"
                            }`}
                          >
                            <label
                              htmlFor="prompt"
                              className="text-sm flex flex-row items-center gap-[3px]"
                            >
                              <span className="text-white/30">
                                {isStory ? "scene 1" : "prompt"}
                              </span>
                              <span className="text-red-600">*</span>
                            </label>
                            <textarea
                              id="prompt"
                              type="text"
                              placeholder="--- Creation beyond imagination... ---"
                              row={50}
                              {...field}
                              className={`w-full h-full break-words no-scrollbar border-[0.5px] border-white/[5%] rounded-[5px] leading-4 tracking-wider placeholder:text-center focus:ring-0 focus:border-neutral-600/30 bg-[rgba(250,250,250,0.02)] text-xs ${
                                meta.touched && meta.error
                                  ? "border-b-red-900/50"
                                  : "border-white/10"
                              }`}
                            />
                          </div>
                        )}
                      </Field>
                      {isStory && (
                        <Field name="prompt2">
                          {({ field, form: { touched, errors }, meta }) => (
                            <div
                              className={`flex-[0.5] mb-5 sm:flex-none ${
                                isStory ? "h-[100px]" : "h-[200px]"
                              }`}
                            >
                              <label
                                htmlFor="prompt2"
                                className="text-sm flex flex-row items-center gap-[3px]"
                              >
                                <span className="text-white/30">scene 2</span>
                              </label>
                              <textarea
                                id="prompt2"
                                type="text"
                                placeholder="--- Creation beyond imagination... ---"
                                row={50}
                                {...field}
                                className={`w-full h-full break-words no-scrollbar border-[0.5px] border-white/[5%] rounded-[5px] leading-4 tracking-wider placeholder:text-center focus:ring-0 focus:border-neutral-600/30 bg-[rgba(250,250,250,0.02)] text-xs ${
                                  meta.touched && meta.error
                                    ? "border-b-red-900/50"
                                    : "border-white/10"
                                }`}
                              />
                            </div>
                          )}
                        </Field>
                      )}
                    </div>
                    <div className="flex flex-col flex-[0.5] sm:flex-none gap-[2px]">
                      <div className="flex flex-col w-full gap-[5px]">
                        <div className="flex flex-col lg:items-start">
                          <label
                            htmlFor="image"
                            className="text-sm flex flex-row items-center gap-[3px]"
                          >
                            <span className="text-white/30">image</span>
                          </label>
                          <div className="flex flex-row items-center">
                            <input
                              id="image"
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              className={`w-full ease-in duration-300 text-sm file:mr-4 file:py-1 file:px-4 file:border-white/[5%] file:rounded-[5px] file:text-sm file:font-semibold file:bg-transparent focus:ring-0 focus:border-neutral-600/30 file:text-brandWhite/30`}
                            />
                            {progress > 0 && (
                              <div className="my-2 text-blue-500 lg:my-0 lg:ml-5">
                                <p className="text-[10px] font-semibold">
                                  {progress}%
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-row items-center gap-3">
                          <div className="w-1/2">
                            <label
                              htmlFor="model"
                              className="text-sm flex flex-row items-center gap-[3px]"
                            >
                              <span className="text-white/30">video model</span>
                            </label>
                            <select
                              onChange={(e) => setVideoModel(e.target.value)}
                              id="model"
                              className={`w-full border-[0.5px] focus:ring-0 focus:border-neutral-600/30 border-white/[5%] rounded-[5px] bg-[rgba(250,250,250,0.02)] text-white/50 text-[10px] leading-[8px]`}
                            >
                              {videoModelOptions.map((model, index) => (
                                <option key={index} value={model.text}>
                                  {model.display}
                                </option>
                              ))}
                            </select>
                          </div>
                          <Field name="promptType">
                            {({ field, meta }) => (
                              <div className="w-1/2">
                                <label
                                  htmlFor="variant"
                                  className="text-sm flex flex-row items-center gap-[3px]"
                                >
                                  <span className="text-white/30">variant</span>
                                  <span className="text-red-600">*</span>
                                </label>
                                <select
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    const value = e.target.value;
                                    console.log("value", value);
                                    if (value === "storyboard") {
                                      setIsStory(true);
                                    } else {
                                      setIsStory(false);
                                    }
                                  }}
                                  id="variant"
                                  className={`w-full border-[0.5px] focus:ring-0 focus:border-neutral-600/30 border-white/[5%] rounded-[5px] bg-[rgba(250,250,250,0.02)] text-[10px] leading-[8px] ${
                                    meta.touched && meta.error
                                      ? "border-b-red-900/50"
                                      : "border-white/10"
                                  }`}
                                >
                                  <option value="" disabled>
                                    ---
                                  </option>
                                  <option value="text">Text Prompt</option>
                                  <option value="image">Image Prompt</option>
                                  <option value="cameo">Cameo Prompt</option>
                                  <option value="storyboard">
                                    Storyboard Prompt
                                  </option>
                                </select>
                              </div>
                            )}
                          </Field>
                        </div>
                      </div>
                      <div className="flex flex-row items-end gap-2">
                        <Field name="aspectRatio">
                          {({ field, meta }) => (
                            <div className="w-1/2">
                              <label
                                htmlFor="ratio"
                                className="text-sm flex flex-row items-center gap-[3px]"
                              >
                                <span className="text-white/30">
                                  aspect ratio
                                </span>
                                <span className="text-red-600">*</span>
                              </label>
                              <select
                                id="ratio"
                                {...field}
                                className={`w-full border-[0.5px] focus:ring-0 focus:border-neutral-600/30 border-white/[5%] rounded-[5px] bg-[rgba(250,250,250,0.02)] text-white/50 text-[10px] leading-[8px] ${
                                  meta.touched && meta.error
                                    ? "border-b-red-900/50"
                                    : "border-white/10"
                                }`}
                              >
                                <option value="" disabled>
                                  ---
                                </option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                              </select>
                            </div>
                          )}
                        </Field>
                        <div className="w-[50%]">
                          <label
                            htmlFor="frames"
                            className="text-sm flex flex-row items-center gap-[3px]"
                          >
                            <span className="text-white/30">frame</span>
                          </label>
                          <select
                            onChange={(e) => {
                              const value = e.target.value;
                              const frame = frameOption.find(
                                (fm) => fm?.value === +value
                              );
                              setFrame(frame);
                            }}
                            id="frames"
                            className={`w-full border-[0.5px] focus:ring-0 focus:border-neutral-600/30 border-white/[5%] rounded-[5px] bg-[rgba(250,250,250,0.02)] text-white/50 text-[10px] leading-[8px]`}
                          >
                            {frameOption.map((frame, index) => (
                              <option key={index} value={frame?.value}>
                                {frame?.key}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="w-[100%] flex mb-3 flex-row items-end gap-2">
                        {!isStory && imageUrl === "" && (
                          <div className="w-1/2">
                            <label
                              htmlFor="quality"
                              className="text-sm flex flex-row items-center gap-[3px]"
                            >
                              <span className="text-white/30">quality</span>
                            </label>
                            <select
                              onChange={(e) => {
                                const qualityObj =
                                  creditOptions?.[frame?.key][e.target.value];
                                setQuality(qualityObj);
                              }}
                              id="quality"
                              className={`w-full border-[0.5px] focus:ring-0 focus:border-neutral-600/30 border-white/[5%] rounded-[5px] bg-[rgba(250,250,250,0.02)] text-white/50 text-[10px] leading-[8px]`}
                            >
                              <option value="standard">
                                {creditOptions?.[frame.key]?.standard?.text}
                              </option>
                              <option value="high">
                                {creditOptions?.[frame.key]?.high?.text}
                              </option>
                            </select>
                          </div>
                        )}
                        <button
                          disabled={loading}
                          type="submit"
                          className={`text-[10px] text-white hover:text-white/40 transition-all duration-1000 ${
                            isStory || imageUrl ? "mt-5 w-full" : "w-1/2"
                          }`}
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              </div>
            </Formik>
          )}
        </div>
      </Layout>
    </>
  );
}

export default HomePage;
